export const SYNC_HISTORY_STATUS_ALL = 'ALL';
export const SYNC_HISTORY_SORT = Object.freeze({ NEWEST: 'desc', OLDEST: 'asc' });

export const syncHistorySources = (entry) => (
  entry?.summary?.sources || entry?.sources || []
);

export const syncHistoryTimestamp = (entry) => (
  entry?.completed_at
  || entry?.summary?.completed_at
  || entry?.updated_at
  || entry?.created_at
  || null
);

const TOKEN_USAGE_AVAILABILITIES = new Set([
  'PENDING', 'EXACT', 'PARTIAL', 'UNAVAILABLE',
]);

const tokenCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : null;
};

const duration = (entry) => tokenCount(
  entry?.summary?.duration_ms ?? entry?.duration_ms,
);

export const syncHistoryTokenUsage = (entry) => {
  const tokenUsage = entry?.token_usage;
  const availability = TOKEN_USAGE_AVAILABILITIES.has(tokenUsage?.availability)
    ? tokenUsage.availability
    : 'UNAVAILABLE';
  return {
    availability,
    promptTokens: tokenCount(tokenUsage?.prompt_tokens),
    completionTokens: tokenCount(tokenUsage?.completion_tokens),
    cachedTokens: tokenCount(tokenUsage?.cached_tokens),
    totalTokens: tokenCount(tokenUsage?.total_tokens),
    callCount: tokenCount(tokenUsage?.call_count) ?? 0,
    unknownUsageCallCount: tokenCount(tokenUsage?.unknown_usage_call_count) ?? 0,
  };
};

export const syncHistoryMetrics = (entry) => {
  const sources = syncHistorySources(entry);
  return {
    sourceCount: sources.length,
    successfulSources: sources.filter((source) => (
      source.status === 'SUCCESS'
    )).length,
    publishedCount: sources.reduce((total, source) => total + Number(source.published_count || 0), 0),
    changedCount: sources.reduce((total, source) => total + Number(source.changed_count || 0), 0),
    durationMs: duration(entry),
    tokenUsage: syncHistoryTokenUsage(entry),
  };
};

export function filterAndSortSyncHistory(entries, { status, direction }) {
  return [...(entries || [])]
    .filter((entry) => status === SYNC_HISTORY_STATUS_ALL || entry.status === status)
    .sort((left, right) => {
      const leftTime = new Date(syncHistoryTimestamp(left) || 0).getTime();
      const rightTime = new Date(syncHistoryTimestamp(right) || 0).getTime();
      const comparison = leftTime - rightTime;
      return direction === SYNC_HISTORY_SORT.OLDEST ? comparison : -comparison;
    });
}

export const paginateSyncHistory = (entries, page, rowsPerPage = 10) => (
  entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
);

/** Return the newest history entry carrying one source result for one FY. */
export const latestSourceHistory = (entries, fiscalYear, sourceType) => {
  const yearEntries = (entries || []).filter(
    (entry) => Number(entry.fiscal_year) === Number(fiscalYear),
  );
  const newestFirst = filterAndSortSyncHistory(yearEntries, {
    status: SYNC_HISTORY_STATUS_ALL,
    direction: SYNC_HISTORY_SORT.NEWEST,
  });
  for (const entry of newestFirst) {
    const source = syncHistorySources(entry).find(
      (item) => item.source_type === sourceType,
    );
    if (source) return { source, timestamp: syncHistoryTimestamp(entry) };
  }
  return null;
};
